/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { Merchant } from "../models";
import * as moment from "moment";

export function validateAmexFee(merchant: Merchant): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value) {
      if (merchant.contractedRates.amexPercent) {
        return null;
      } else {
        return { invalidAmexFee: true };
      }
    } else return null;
  };
}

export function requiredTrimmed(control: AbstractControl): ValidationErrors | null {
  return typeof control.value === "string" && control.value.trim().length > 0 ? null : { required: true };
}

export function minSelected(min: number): ValidatorFn {
  return (abstractControl: AbstractControl) => {
    if (abstractControl instanceof FormArray) {
      const totalSelected = abstractControl.controls
        .map((control) => control.value)
        .reduce((prev, next) => (next ? prev + next : prev), 0);

      return totalSelected >= min ? null : { required: true };
    }
    if (abstractControl instanceof FormGroup) {
      const totalSelected: number = Object.keys(abstractControl.controls)
        .map((key) => abstractControl.get(key)?.value)
        .reduce((prev, next) => (next ? prev + next : prev), 0);
      return totalSelected >= min ? null : { required: true };
    }
    throw new Error("abstractControl is not an instance of FormArray of FormGroup");
  };
}

export function passwordMatchValidator(controlName: string, matchingControlName: string): ValidatorFn {
  return (abstractControl: AbstractControl): ValidationErrors | null => {
    const formGroup = abstractControl as FormGroup;
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];
    if (control.value !== matchingControl.value) {
      return { passwordNotEqual: true };
    }
    return null;
  };
}

export function accountNumberValidation(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.get("accountDetails")?.get("accountUsage")?.value !== "Combined") {
      if (
        control.get("accountDetails")?.get("accountNumber")?.value ===
        control.get("otherAccountDetails")?.get("accountNumber")?.value
      ) {
        control.get("otherAccountDetails")?.get("accountNumber")?.setErrors({ invalidAccountDetails: true });
        return { invalidAccountDetails: true };
      }
    }

    if (control.get("otherAccountDetails")?.get("accountNumber")?.hasError("invalidAccountDetails")) {
      control.get("otherAccountDetails")?.get("accountNumber")?.setErrors(null);
      control.get("otherAccountDetails")?.get("accountNumber")?.markAsPristine();
      control.get("otherAccountDetails")?.get("accountNumber")?.markAsUntouched();
    }
    return null;
  };
}

export function midLowerThanQualified(qualifiedUpperBoundary: string, midQualifiedUpperBoundary: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    const qualified = formGroup.controls[qualifiedUpperBoundary];
    const midQualified = formGroup.controls[midQualifiedUpperBoundary];
    if (qualified.value > midQualified.value) {
      return { midBoundaryLower: true };
    }
    return null;
  };
}

export function nonQualifiedLowerThanMidQualified(
  midQualifiedUpperBoundary: string,
  nonQualifiedUpperBoundary: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    const midQualified = formGroup.controls[midQualifiedUpperBoundary];
    const nonQualified = formGroup.controls[nonQualifiedUpperBoundary];
    if (midQualified.value > nonQualified.value) {
      return { nonQualifiedBoundaryLower: true };
    }
    return null;
  };
}

export function ValidateRange(dateFrom: string, dateTo: string, range: number): ValidatorFn {
  return (abstractControl: AbstractControl): ValidationErrors | null => {
    const formGroup = abstractControl as FormGroup;
    const startDate = formGroup.controls[dateFrom];
    const endDate = formGroup.controls[dateTo];
    if (Math.abs(moment(startDate.value).diff(moment(endDate.value), "days")) >= range) {
      return { dateRange: true };
    }
    return null;
  };
}

export function ValidateDateSelected(dateFrom: string, dateTo: string): ValidatorFn {
  return (abstractControl: AbstractControl): ValidationErrors | null => {
    const formGroup = abstractControl as FormGroup;
    const startDate = formGroup.controls[dateFrom];
    const endDate = formGroup.controls[dateTo];
    if (moment(endDate.value).isBefore(moment(startDate.value))) {
      return { endDateGreaterThanStartDate: true };
    }
    return null;
  };
}
